/* eslint-disable array-callback-return */
import { useTheme } from '@emotion/react';
import { Typography } from '@mui/material';
import { formatMoneyAmount } from 'framework/base/components/AryNumberFormatter';
import { GET_TRADE_RATE_CURRENCIES } from 'framework/base/constants/aryGraphqlSchemas';
import { useAryQuery } from 'framework/base/hooks/useAryGraphql';
import useShowCurrency from 'framework/base/hooks/useShowCurrency';
import useSystemRate from 'framework/base/hooks/useSystemRate';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import React, { useEffect } from 'react';

// ! WARNING: source currency code in here means the money that is being sent and is reduced from the user's account. so it means if we are buying dollars with a rate of 56,000 while the real rate is 60,000 then we are making profit. (source is "USD" and destination is "IRT")

const ProfitRate = ({
  sourceCurrencyCode,
  sourceAmountValue,
  destinationCurrencyCode,
  enteredTradeRate,
  isRateMinusEnteredRate,
  destinationAmountValue,
  profitRateProps
}) => {
  const theme = useTheme();
  const { t } = useFrameworkTranslation();
  const showCurrency = useShowCurrency();
  const { sourceAmount, fetchedSystemRate } = useSystemRate({
    baseCurrencyCode: sourceCurrencyCode,
    destinationCurrencyCode
  });

  const systemEquivalentAmount = fetchedSystemRate * sourceAmountValue;

  const profit = destinationAmountValue - systemEquivalentAmount;

  let { data: tradeRate } = useAryQuery({
    gqlConfig: GET_TRADE_RATE_CURRENCIES,
    variables: {
      source: sourceCurrencyCode,
      destination: destinationCurrencyCode
    },
    skip:
      !sourceCurrencyCode ||
      !destinationCurrencyCode ||
      destinationCurrencyCode === sourceCurrencyCode
  });

  if (tradeRate < 1) {
    tradeRate = 1 / tradeRate;
  }

  if (sourceAmount && enteredTradeRate) {
    enteredTradeRate = sourceAmount / enteredTradeRate;
  }

  useEffect(() => {
    if (!Number.isNaN(profit)) {
      profitRateProps?.profitOnChange?.(
        isRateMinusEnteredRate ? -1 * profit : profit
      );
    }
  }, [profit]);

  useEffect(() => {
    if (destinationCurrencyCode) {
      profitRateProps?.profitCodeOnChange?.(destinationCurrencyCode);
    }
  }, [destinationCurrencyCode]);

  return sourceCurrencyCode && destinationCurrencyCode && enteredTradeRate ? (
    <Typography
      variant="c2Bold"
      sx={{
        display: 'flex',
        gap: '0.5rem',
        color:
          profit < 0
            ? isRateMinusEnteredRate
              ? theme.text.green.color
              : theme.text.red.color
            : isRateMinusEnteredRate
            ? theme.text.red.color
            : theme.text.green.color
      }}
    >
      {Number.isNaN(profit) ? 0 : formatMoneyAmount(Math.abs(profit), 2, true)}{' '}
      {showCurrency({ code: destinationCurrencyCode })}
    </Typography>
  ) : (
    <Typography
      variant="c2Bold"
      sx={{
        color: theme.text.secondary.color
      }}
    >
      {t('Calculating Profit and Loss')}
    </Typography>
  );
};

export default ProfitRate;
